import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContextWeb } from "../context/AuthContextWeb";
import { apiRequest } from "../lib/apiClient";

export default function AccountPage() {
  const { user, logout } = useContext(AuthContextWeb);
  const navigate = useNavigate();

  /* ── Profile fetch ── */
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  /* ── Edit mode ── */
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: "",
    phone: "",
    address: "",
    dateOfBirth: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  /* ── Delete ── */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!user?.email) return;
    setProfileLoading(true);
    setProfileError("");
    const encodedEmail = encodeURIComponent(user.email);
    apiRequest(`/users/${encodedEmail}`)
      .then((data) => {
        setProfile(data);
        setEditForm({
          fullName: data.fullName || "",
          phone: data.phone || "",
          address: data.address || "",
          dateOfBirth: data.dateOfBirth || "",
        });
      })
      .catch((err) =>
        setProfileError(err?.message || "Failed to load profile."),
      )
      .finally(() => setProfileLoading(false));
  }, [user?.email]);

  const handleSaveProfile = async () => {
    if (!user?.email) return;
    setSaving(true);
    setSaveError("");
    try {
      await apiRequest(`/users/${encodeURIComponent(user.email)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      setProfile((prev) => ({ ...prev, ...editForm }));
      setEditing(false);
    } catch (err) {
      setSaveError(err?.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") return;
    if (!profile?.id) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiRequest(`/users/delete/${profile.id}`, { method: "DELETE" });
      await logout();
      navigate("/app/login");
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete account.");
      setDeleting(false);
    }
  };

  return (
    <main className="account-page">
      {/* ── Profile ── */}
      <div className="acct-block">
        <div className="acct-block-header">Profile</div>
        <div className="acct-block-body">
          {profileLoading ? (
            <p className="loading-message">Loading profile…</p>
          ) : profileError ? (
            <p className="form-error">{profileError}</p>
          ) : editing ? (
            <div className="acct-meta-list">
              <div className="acct-meta-row">
                <span className="acct-meta-label">Name</span>
                <input
                  className="acct-input-sm"
                  value={editForm.fullName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, fullName: e.target.value }))
                  }
                />
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Phone</span>
                <input
                  className="acct-input-sm"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, phone: e.target.value }))
                  }
                />
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Address</span>
                <input
                  className="acct-input-sm"
                  value={editForm.address}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, address: e.target.value }))
                  }
                />
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Date of Birth</span>
                <input
                  className="acct-input-sm"
                  type="date"
                  value={editForm.dateOfBirth}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, dateOfBirth: e.target.value }))
                  }
                />
              </div>
              {saveError && <p className="form-error">{saveError}</p>}
              <div
                className="acct-inline-edit"
                style={{ padding: "0.5rem 0.75rem" }}
              >
                <button
                  className="acct-edit-action"
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  className="acct-edit-action acct-edit-cancel"
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setSaveError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="acct-meta-list">
              <div className="acct-meta-row">
                <span className="acct-meta-label">Name</span>
                <span className="acct-meta-value">
                  {profile?.fullName || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Email</span>
                <span className="acct-meta-value">
                  {profile?.email || user?.email || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Phone</span>
                <span className="acct-meta-value">{profile?.phone || "—"}</span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Address</span>
                <span className="acct-meta-value">
                  {profile?.address || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Date of Birth</span>
                <span className="acct-meta-value">
                  {profile?.dateOfBirth || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">Role</span>
                <span className="acct-meta-value">
                  {profile?.role || user?.role || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span className="acct-meta-label">KYC Status</span>
                <span
                  className={`acct-meta-value acct-kyc acct-kyc--${(
                    profile?.kycStatus || ""
                  ).toLowerCase()}`}
                >
                  {profile?.kycStatus || "—"}
                </span>
              </div>
              <div className="acct-meta-row">
                <span />
                <button
                  className="acct-edit-action"
                  type="button"
                  onClick={() => setEditing(true)}
                >
                  Edit Profile
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Account Actions ── */}
      <div className="acct-block">
        <div className="acct-block-header">Account Actions</div>
        <div className="acct-block-body acct-actions-body">
          <button className="btn btn-logout" type="button" onClick={logout}>
            Logout
          </button>
          {!showDeleteConfirm ? (
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => {
                setDeleteConfirmText("");
                setDeleteError("");
                setShowDeleteConfirm(true);
              }}
            >
              Delete Account
            </button>
          ) : (
            <div className="acct-delete-confirm">
              <p className="acct-delete-warning">
                Type <strong>DELETE</strong> to permanently remove your account.
                This cannot be undone.
              </p>
              <input
                className="acct-input-sm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                autoFocus
              />
              {deleteError && <p className="form-error">{deleteError}</p>}
              <div className="acct-delete-actions">
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={handleDelete}
                  disabled={
                    deleting ||
                    deleteConfirmText.trim().toUpperCase() !== "DELETE"
                  }
                >
                  {deleting ? "Deleting…" : "Confirm Delete"}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
